/**
 * KasszaPage — Cash Register Main Page
 * Story 62-12: Cash Management Frontend UI
 *
 * Napi kassza osszesito: 3 zseb egyenleg, tranzakcio lista, kassza nyitas.
 */

import { classifyRecordTxError, type RecordTxError } from '@/api/cash-management';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  useCashSummary,
  useOpenRegister,
  useRecordTransaction,
  useTodayRegister,
} from '@/hooks/useCashManagement';
import { useTranslation } from '@kgc/i18n';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const POCKET_COLORS: Record<string, string> = {
  REGISTER: 'border-green-500 bg-green-50 dark:bg-green-900/20',
  DEPOSIT: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  CREDIT: 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function KasszaPage() {
  const { t } = useTranslation();

  const POCKET_LABELS: Record<string, string> = {
    REGISTER: t('finance.cashRegister'),
    DEPOSIT: t('finance.deposit'),
    CREDIT: t('finance.szhLabel'),
  };
  const navigate = useNavigate();
  const {
    register,
    isLoading: registerLoading,
    error: registerError,
    refetch,
  } = useTodayRegister();
  const {
    summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useCashSummary(register?.id);
  const openRegister = useOpenRegister();
  const registerId = register?.id ?? '';
  const recordTx = useRecordTransaction(registerId);

  const [txForm, setTxForm] = useState({
    pocketType: 'REGISTER' as 'REGISTER' | 'DEPOSIT' | 'CREDIT',
    amount: '',
    direction: 'IN' as 'IN' | 'OUT',
    note: '',
    expenseCategory: '' as string,
  });
  const [showSzhConfirm, setShowSzhConfirm] = useState(false);
  const [szhNoteError, setSzhNoteError] = useState(false);
  // 67-kör 1️⃣: a rögzítés hibája eddig NÉMA volt (nincs onError, nincs globális
  // MutationCache) — a pultos csak a lista hiányából sejthette, hogy nem ment el.
  const [recordError, setRecordError] = useState<RecordTxError | null>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const noteRef = useRef<HTMLInputElement>(null);

  const handleOpenRegister = async () => {
    await openRegister.mutateAsync(true);
    refetch();
  };

  /** 409 utáni kiút: a pultos frissíti a listát, és ellenőrzi, mi történt közben. */
  const handleRecordErrorRefresh = () => {
    setRecordError(null);
    refetch();
    refetchSummary();
  };

  const handleRecordTransactionRequest = () => {
    setRecordError(null);
    if (!registerId) return;
    const amount = parseInt(txForm.amount, 10);
    if (!amount || amount <= 0 || !Number.isInteger(amount)) return;

    // SZH (CREDIT pocket) transactions require confirmation
    if (txForm.pocketType === 'CREDIT') {
      // Internal SZH movements require a note (no voucher needed)
      if (!txForm.note.trim()) {
        setSzhNoteError(true);
        return;
      }
      setSzhNoteError(false);
      setShowSzhConfirm(true);
      return;
    }

    setSzhNoteError(false);
    void executeRecordTransaction();
  };

  const executeRecordTransaction = async () => {
    setShowSzhConfirm(false);
    if (!registerId) return;
    const amount = parseInt(txForm.amount, 10);
    if (!amount || amount <= 0 || !Number.isInteger(amount)) return;

    const txData: {
      pocketType: 'REGISTER' | 'DEPOSIT' | 'CREDIT';
      amount: number;
      direction: 'IN' | 'OUT';
      note?: string;
      expenseCategory?: string;
    } = {
      pocketType: txForm.pocketType,
      amount,
      direction: txForm.direction,
    };
    if (txForm.note) txData.note = txForm.note;
    if (txForm.direction === 'OUT' && txForm.expenseCategory) {
      txData.expenseCategory = txForm.expenseCategory;
    }

    try {
      await recordTx.mutateAsync(txData);
    } catch (err) {
      // A bevitt állapotot SZÁNDÉKOSAN nem ürítjük — a pultosnak nem kell újragépelnie.
      const classified = classifyRecordTxError(err);
      setRecordError(classified);
      if (classified.field === 'amount') amountRef.current?.focus();
      if (classified.field === 'note') noteRef.current?.focus();
      return;
    }

    setRecordError(null);
    setTxForm({
      pocketType: 'REGISTER',
      amount: '',
      direction: 'IN',
      note: '',
      expenseCategory: '',
    });
    setSzhNoteError(false);
  };

  // No register open
  if (!registerLoading && (registerError || !register)) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.cashRegister')}</h1>
        <Card>
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">{t('finance.noRegisterOpen')}</p>
            <Button onClick={handleOpenRegister} disabled={openRegister.isPending}>
              {openRegister.isPending ? t('finance.opening') : t('finance.openRegister')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">
          {t('finance.cashRegister')} —{' '}
          {register?.date ? new Date(register.date).toLocaleDateString('hu-HU') : 'Ma'}
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/finance/cash/szh')}>
            {t('finance.szhFolderButton')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/finance/cash/close')}>
            {t('finance.dailyCloseButton')}
          </Button>
        </div>
      </div>

      {/* Pocket cards */}
      {summaryLoading ? (
        <div className="text-center py-8 text-muted-foreground">{t('finance.loadingData')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {summary?.pockets.map(pocket => (
              <Card key={pocket.type} className={`border-l-4 ${POCKET_COLORS[pocket.type] ?? ''}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">
                    {POCKET_LABELS[pocket.type] ?? pocket.type}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{formatCurrency(pocket.currentBalance)}</div>
                  <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                    <span>
                      {t('finance.inLabel')} {formatCurrency(pocket.totalIn)}
                    </span>
                    <span>
                      {t('finance.outLabel')} {formatCurrency(pocket.totalOut)}
                    </span>
                    <span>{pocket.transactionCount} trx</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Total */}
          <Card className="mb-6">
            <CardContent className="py-4">
              <div className="flex justify-between items-center">
                <span className="font-medium text-lg">{t('finance.totalBalance')}</span>
                <span className="text-2xl font-bold">
                  {formatCurrency(summary?.totalBalance ?? 0)}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Transaction form */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>{t('finance.recordTransaction')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <select
                  className="border rounded px-3 py-2 bg-background"
                  value={txForm.pocketType}
                  onChange={e =>
                    setTxForm({
                      ...txForm,
                      pocketType: e.target.value as 'REGISTER' | 'DEPOSIT' | 'CREDIT',
                    })
                  }
                >
                  <option value="REGISTER">{t('finance.cashRegister')}</option>
                  <option value="DEPOSIT">{t('finance.deposit')}</option>
                  <option value="CREDIT">{t('finance.szhLabel')}</option>
                </select>
                <select
                  className="border rounded px-3 py-2 bg-background"
                  value={txForm.direction}
                  onChange={e =>
                    setTxForm({ ...txForm, direction: e.target.value as 'IN' | 'OUT' })
                  }
                >
                  <option value="IN">{t('finance.income')}</option>
                  <option value="OUT">{t('finance.expense')}</option>
                </select>
                <Input
                  ref={amountRef}
                  type="number"
                  data-testid="cash-tx-amount"
                  placeholder={t('finance.amountPlaceholder')}
                  value={txForm.amount}
                  onChange={e => setTxForm({ ...txForm, amount: e.target.value })}
                  className={recordError?.field === 'amount' ? 'border-red-500' : ''}
                />
                <Input
                  ref={noteRef}
                  data-testid="cash-tx-note"
                  placeholder={
                    txForm.pocketType === 'CREDIT'
                      ? t('finance.szh.internalNote')
                      : t('finance.notePlaceholder')
                  }
                  value={txForm.note}
                  onChange={e => {
                    setTxForm({ ...txForm, note: e.target.value });
                    if (e.target.value.trim()) setSzhNoteError(false);
                  }}
                  className={szhNoteError || recordError?.field === 'note' ? 'border-red-500' : ''}
                />
                <Button onClick={handleRecordTransactionRequest} disabled={recordTx.isPending}>
                  {t('finance.record')}
                </Button>
              </div>
              {/* SALES-FG-4-2: Expense category selector — visible when direction is OUT */}
              {txForm.direction === 'OUT' && (
                <div className="mt-3">
                  <label className="text-sm font-medium text-muted-foreground mr-2">
                    {t('finance.expenseCategory.label')}
                  </label>
                  <select
                    className="border rounded px-3 py-2 bg-background text-sm"
                    value={txForm.expenseCategory}
                    onChange={e => setTxForm({ ...txForm, expenseCategory: e.target.value })}
                  >
                    <option value="">{t('finance.expenseCategory.none')}</option>
                    <option value="SALARY">{t('finance.expenseCategory.salary')}</option>
                    <option value="SUPPLIER_PAYMENT">
                      {t('finance.expenseCategory.supplierPayment')}
                    </option>
                    <option value="OFFICE">{t('finance.expenseCategory.office')}</option>
                    <option value="TRANSPORT">{t('finance.expenseCategory.transport')}</option>
                    <option value="MARKETING">{t('finance.expenseCategory.marketing')}</option>
                    <option value="OTHER">{t('finance.expenseCategory.other')}</option>
                  </select>
                </div>
              )}
              {szhNoteError && (
                <p className="text-sm text-red-500 mt-2">{t('finance.szh.internalNoteRequired')}</p>
              )}

              {/* 67-kör 1️⃣: rögzítési hiba — a pultos SOHA nem lát státuszkód-számot,
                  és a 409 sosem az ő hibája. A bevitt adatok megmaradnak. */}
              {recordError && (
                <div
                  data-testid="cash-tx-record-error"
                  role="alert"
                  className="mt-3 rounded border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-900/20"
                >
                  {recordError.kind === 'conflict' && (
                    <div data-testid="cash-tx-record-conflict">
                      <p className="text-sm font-medium text-red-700 dark:text-red-300">
                        {t('finance.recordError.conflictTitle')}
                      </p>
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                        {t('finance.recordError.conflictBody')}
                      </p>
                      <Button
                        variant="outline"
                        className="mt-2"
                        data-testid="cash-tx-record-refresh"
                        onClick={handleRecordErrorRefresh}
                      >
                        {t('finance.recordError.refresh')}
                      </Button>
                    </div>
                  )}
                  {recordError.kind === 'validation' && (
                    <p
                      data-testid="cash-tx-record-validation"
                      className="text-sm text-red-600 dark:text-red-400"
                    >
                      {recordError.field === 'amount'
                        ? t('finance.recordError.amountInvalid')
                        : recordError.field === 'note'
                          ? t('finance.recordError.noteInvalid')
                          : t('finance.recordError.validation')}
                    </p>
                  )}
                  {recordError.kind === 'failed' && (
                    <p
                      data-testid="cash-tx-record-failed"
                      className="text-sm text-red-600 dark:text-red-400"
                    >
                      {t('finance.recordError.failed')}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Transaction list — loading/error/empty/list kizáró ágak (53Y cash-ENABLE UX-hardening) */}
          {summaryError && (
            <Card
              data-testid="cash-tx-error"
              className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
            >
              <CardContent className="py-8 text-center">
                <p className="text-red-600 dark:text-red-400 mb-4">
                  {t('page.errorPrefix', { message: summaryError })}
                </p>
                <Button onClick={() => refetchSummary()}>{t('common.retry')}</Button>
              </CardContent>
            </Card>
          )}

          {!summaryError && summary && summary.transactions.length === 0 && (
            <Card data-testid="cash-tx-empty">
              <CardContent className="py-8 text-center text-muted-foreground">
                {t('finance.cash.noTransactions')}
              </CardContent>
            </Card>
          )}

          {!summaryError && summary && summary.transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{t('finance.transactions')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="divide-y">
                  {summary.transactions.map(tx => (
                    <div key={tx.id} className="py-3 flex justify-between items-center">
                      <div>
                        <span
                          className={`font-medium ${tx.direction === 'IN' ? 'text-green-600' : 'text-red-600'}`}
                        >
                          {tx.direction === 'IN' ? '+' : '-'}
                          {formatCurrency(tx.amount)}
                        </span>
                        <span className="ml-3 text-muted-foreground text-sm">{tx.category}</span>
                        {tx.note && (
                          <span className="ml-2 text-muted-foreground text-sm">— {tx.note}</span>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {new Date(tx.createdAt).toLocaleTimeString('hu-HU')}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* SZH Transaction Confirmation Dialog */}
      {showSzhConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-slate-800">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              {t('finance.szh.confirmTitle')}
            </h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{t('finance.szh.confirmBody')}</p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowSzhConfirm(false)}>
                {t('finance.szh.cancel')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => void executeRecordTransaction()}
                disabled={recordTx.isPending}
              >
                {t('finance.szh.confirm')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
